import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import { collection, getDocs, limit, orderBy, query, startAfter } from 'firebase/firestore'
import { firestore } from '../../lib/db'
import { subscribeMessages } from '../../lib/rt'
import { useQuery } from '@tanstack/react-query'
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { useAuth } from '../../auth/AuthContext'
import { isImageFile, uploadChatImage } from '../../lib/storage'
import type { Chat, Message, Project, TeamMember } from '../../lib/types'
import { armarMencionables, fueraEnTextoDeChat } from '../proyecto/fueraDelProyecto'
import { esDeCasa } from '../proyecto/miembros'
import { AvisoFueraDelProyecto } from '../proyecto/AvisoFueraDelProyecto'
import { filtrarMencionables, type Mencionable } from '../rich/mentions'
import { IconoCerrar, AvatarCluster } from '../common'
import { BuzzBar } from '../buzz/BuzzBar'
import { dayKey, dayLabel, nameOf, timeOf } from './helpers'
import { TextoMensaje } from './TextoMensaje'
import { MembersModal } from './MembersModal'
import { ChipBasecamp, MarcaExterno, useAutor } from '../autoria'
import './chat.css'

// Conversación (página /chat/:id y tab de proyecto embebido, mismo componente).
// Mensajes por listener (rt.subscribeMessages, últimos 50); paginación hacia atrás
// con getDocs + startAfter (lectura puntual del mismo path del listener — permitido).

const PAGE = 50

interface PendingMsg {
  temp: Message
  realId?: string
}

export interface ConversationProps {
  chatId: string
  chat: Chat | null // doc de la bandeja, o stub del proyecto en modo embebido
  title: string
  meEmail: string
  meName: string
  names: Map<string, string>
  team: TeamMember[]
  muted?: boolean // undefined = sin toggle (embebido)
  embedded?: boolean
  onDenied: () => void // permission-denied del listener (G-7)
  onBack?: () => void
  onClose?: () => void // ✕ del header: cerrar el panel de la burbuja (solo en ese modo)
  onLeft?: () => void
  toast: (msg: string) => void
  /**
   * El proyecto, cuando quien monta ya lo tiene (tab embebido). Con él, el @ ofrece
   * también al resto del equipo marcado "no está en el proyecto" y, al enviar, se
   * avisa y se ofrece agregarlo. Sin él, si el chat ES de proyecto (bandeja,
   * burbuja) se lee con la misma llave que la página del proyecto; en dm y grupos
   * no hay proyecto al cual agregar y el @ sigue siendo solo de los miembros.
   */
  proyecto?: Project
}

export function Conversation({
  chatId,
  chat,
  title,
  meEmail,
  meName,
  names,
  team,
  muted,
  embedded,
  onDenied,
  onBack,
  onClose,
  onLeft,
  toast,
  proyecto: proyectoDado,
}: ConversationProps) {
  const { me } = useAuth()
  const autor = useAutor()
  // El chat de un proyecto abierto desde la bandeja o la burbuja no trae el
  // Project, y "que me sugiera añadirlo" aplica igual ahí: se lee con qk.project
  // (misma forma { project, etag } que la página del proyecto, así que comparten
  // caché y el alta del aviso se ve en los dos lados). Solo para quien es de casa:
  // a un invitado no se le ofrece gente de fuera (ver armarMencionables).
  const pidDelChat = chat?.type === 'project' ? chat.projectId : undefined
  const proyectoQ = useQuery({
    queryKey: qk.project(pidDelChat ?? ''),
    queryFn: () => apiGet<{ project: Project; etag: string }>(`/api/projects/${pidDelChat}`),
    enabled: !proyectoDado && Boolean(pidDelChat) && esDeCasa(me),
    staleTime: 60_000,
  })
  const proyecto = esDeCasa(me) ? (proyectoDado ?? proyectoQ.data?.project) : undefined
  const [live, setLive] = useState<Message[] | null>(null)
  const [older, setOlder] = useState<Message[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [pending, setPending] = useState<PendingMsg[]>([])
  const [draft, setDraft] = useState('')
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(null)
  const [showMembers, setShowMembers] = useState(false)
  const [mention, setMention] = useState<{ query: string; start: number } | null>(null)
  const [mentionIdx, setMentionIdx] = useState(0)
  /** Arrobados de fuera del proyecto en lo que ya se envió (chat del proyecto). */
  const [fueraEnviados, setFueraEnviados] = useState<string[]>([])

  const listRef = useRef<HTMLDivElement>(null)
  const taRef = useRef<HTMLTextAreaElement>(null)
  const stickBottom = useRef(true)
  const lastReadSent = useRef(0)
  const onDeniedRef = useRef(onDenied)
  onDeniedRef.current = onDenied

  // ---- listener de mensajes (uno solo a la vez; se cierra al cambiar de chat) ----
  useEffect(() => {
    setLive(null)
    setOlder([])
    setHasMore(true)
    setPending([])
    setEditing(null)
    setMention(null)
    setFueraEnviados([])
    stickBottom.current = true
    lastReadSent.current = 0
    return subscribeMessages(chatId, setLive, () => onDeniedRef.current())
  }, [chatId])

  // ---- marcar leído (throttle 5 s) al abrir, al enfocar y al llegar mensajes ----
  const markRead = useCallback(() => {
    if (Date.now() - lastReadSent.current < 5000) return
    lastReadSent.current = Date.now()
    void apiPost(`/api/chats/${chatId}/read`).catch(() => {})
  }, [chatId])

  useEffect(() => {
    if (live !== null && document.hasFocus()) markRead()
  }, [live, markRead])

  useEffect(() => {
    const onFocus = () => markRead()
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [markRead])

  // ---- lista combinada: páginas anteriores + listener + optimistas ----
  const liveIds = useMemo(() => new Set((live ?? []).map((m) => m.id)), [live])

  useEffect(() => {
    // El optimista se retira cuando su mensaje real ya llegó por el listener.
    setPending((p) => (p.some((e) => e.realId && liveIds.has(e.realId)) ? p.filter((e) => !(e.realId && liveIds.has(e.realId))) : p))
  }, [liveIds])

  const messages = useMemo(() => {
    const seen = new Set<string>()
    const all: Message[] = []
    for (const m of [...older, ...(live ?? [])]) {
      if (seen.has(m.id)) continue
      seen.add(m.id)
      all.push(m)
    }
    for (const e of pending) {
      if (e.realId && seen.has(e.realId)) continue
      all.push(e.temp)
    }
    return all
  }, [older, live, pending])

  useEffect(() => {
    const el = listRef.current
    if (el && stickBottom.current) el.scrollTop = el.scrollHeight
  }, [messages, chatId])

  const onScroll = () => {
    const el = listRef.current
    if (!el) return
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  // ---- paginación hacia atrás (getDocs con cursor, 50 en 50) ----
  const loadOlder = async () => {
    const first = messages[0]
    if (!first || loadingOlder) return
    setLoadingOlder(true)
    try {
      const q = query(
        collection(firestore, `chats/${chatId}/messages`),
        orderBy('at', 'desc'),
        startAfter(first.at),
        limit(PAGE),
      )
      const snap = await getDocs(q)
      const batch = snap.docs.map((d) => d.data() as Message).reverse()
      if (batch.length < PAGE) setHasMore(false)
      const el = listRef.current
      const prevH = el?.scrollHeight ?? 0
      stickBottom.current = false
      setOlder((prev) => [...batch, ...prev])
      requestAnimationFrame(() => {
        if (el) el.scrollTop += el.scrollHeight - prevH
      })
    } catch {
      toast('No se pudieron cargar mensajes anteriores')
    } finally {
      setLoadingOlder(false)
    }
  }

  // ---- adjunto: SOLO imágenes, comprimidas a WebP antes de subir ----
  // Se sube al elegirla, no al enviar: así el envío es inmediato y el error de
  // subida se ve mientras todavía se está redactando.
  const [adjunto, setAdjunto] = useState<{ url: string; nombre: string } | null>(null)
  const [subiendo, setSubiendo] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const adjuntar = async (file: File | undefined) => {
    if (!file) return
    if (!isImageFile(file)) {
      toast('Solo se pueden adjuntar imágenes')
      return
    }
    setSubiendo(true)
    try {
      const url = await uploadChatImage(chatId, file)
      setAdjunto({ url, nombre: file.name })
    } catch (e) {
      toast(e instanceof Error ? e.message : 'No se pudo subir la imagen')
    } finally {
      setSubiendo(false)
    }
  }

  // ---- envío con append optimista ----
  const send = async () => {
    const text = draft.trim()
    const imageUrl = adjunto?.url ?? null
    if (!text && !imageUrl) return
    if (text.length > 4000) {
      toast('El mensaje no puede pasar de 4000 caracteres')
      return
    }
    const temp: Message = {
      id: `tmp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
      text,
      imageUrl,
      by: meEmail,
      byName: meName,
      at: new Date().toISOString(),
      mentions: [],
      editedAt: null,
      deletedAt: null,
    }
    // Arrobados de fuera del proyecto: el API resuelve las @ SOLO contra los
    // miembros del chat, así que a ellos no les llega nada. Se avisa al enviar (no
    // mientras se escribe: en un chat se envía con Enter y el aviso parpadearía), y
    // solo si el envío salió: si falla, el texto vuelve al composer y no hubo
    // mención que "no llegó".
    const fuera = proyecto && text ? fueraEnTextoDeChat(text, mentionCandidates) : []
    setDraft('')
    setAdjunto(null)
    setMention(null)
    setPending((p) => [...p, { temp }])
    stickBottom.current = true
    try {
      const { message } = await apiPost<{ message: Message }>(`/api/chats/${chatId}/messages`, { text, imageUrl })
      setPending((p) => p.map((e) => (e.temp.id === temp.id ? { ...e, realId: message.id } : e)))
      if (fuera.length > 0) setFueraEnviados((prev) => [...new Set([...prev, ...fuera])])
    } catch {
      setPending((p) => p.filter((e) => e.temp.id !== temp.id))
      setDraft(text) // que no se pierda lo escrito
      if (imageUrl) setAdjunto({ url: imageUrl, nombre: 'imagen' }) // ni la imagen ya subida
      toast('No se pudo enviar el mensaje')
    }
  }

  // ---- editar / eliminar propios (admin de grupo también puede) ----
  const isGroupAdmin = chat?.type === 'group' && (chat.admins ?? []).includes(meEmail)

  const saveEdit = async () => {
    if (!editing) return
    const text = editing.text.trim()
    if (!text) return
    try {
      await apiPatch(`/api/chats/${chatId}/messages/${editing.id}`, { text })
      setEditing(null)
    } catch {
      toast('No se pudo editar el mensaje')
    }
  }

  const removeMsg = async (mid: string) => {
    if (!window.confirm('¿Eliminar este mensaje?')) return
    try {
      await apiDelete(`/api/chats/${chatId}/messages/${mid}`)
    } catch {
      toast('No se pudo eliminar el mensaje')
    }
  }

  // Buzz en un mensaje: el PUT actualiza el doc del mensaje y el listener refresca la
  // burbuja solo (sin invalidación ni optimistic — la lista viene por realtime, D-05).
  const buzzMsg = (mid: string, text: string | null) => {
    void apiPut(`/api/chats/${chatId}/messages/${mid}/buzz`, { text }).catch(() =>
      toast('No se pudo enviar el buzz'),
    )
  }

  // ---- @autocomplete del composer ----
  // En el chat del proyecto van los miembros del canal y DETRÁS el resto del equipo
  // marcado de fuera (misma lista que el message board). En cualquier otro chat,
  // solo los miembros del chat, como siempre.
  const mentionCandidates = useMemo<Mencionable[]>(
    () =>
      proyecto
        ? armarMencionables(chat?.members ?? [], team, meEmail)
        : (chat?.members ?? [])
            .filter((e) => e !== meEmail)
            .map((e) => ({ email: e, name: nameOf(names, e) })),
    [chat, meEmail, names, proyecto, team],
  )

  const mentionMatches = useMemo(
    () => (mention ? filtrarMencionables(mentionCandidates, mention.query) : []),
    [mention, mentionCandidates],
  )

  const detectMention = (value: string, caret: number) => {
    const upto = value.slice(0, caret)
    const at = upto.lastIndexOf('@')
    if (at === -1 || (at > 0 && !/\s/.test(upto[at - 1] ?? ''))) {
      setMention(null)
      return
    }
    const q = upto.slice(at + 1)
    if (q.includes('\n') || q.length > 30) {
      setMention(null)
      return
    }
    setMention({ query: q, start: at })
    setMentionIdx(0)
  }

  const pickMention = (m: { email: string; name: string }) => {
    if (!mention) return
    const ta = taRef.current
    const caret = ta?.selectionStart ?? draft.length
    const next = `${draft.slice(0, mention.start)}@${m.name} ${draft.slice(caret)}`
    setDraft(next)
    setMention(null)
    requestAnimationFrame(() => {
      if (!ta) return
      ta.focus()
      const pos = mention.start + m.name.length + 2
      ta.setSelectionRange(pos, pos)
    })
  }

  const onComposerKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionIdx((i) => (i + 1) % mentionMatches.length)
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionIdx((i) => (i - 1 + mentionMatches.length) % mentionMatches.length)
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        pickMention(mentionMatches[mentionIdx] ?? mentionMatches[0]!)
        return
      }
      if (e.key === 'Escape') {
        // El Escape es de ESTA capa: se cierra el desplegable de @menciones y ahí
        // muere. Sin stopPropagation seguía hasta el listener de documento del
        // panel del chat y cerraba el chat entero, tirando el borrador escrito.
        e.preventDefault()
        e.stopPropagation()
        setMention(null)
        return
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      void send()
    }
  }

  // textarea autoresize (hasta ~4 líneas)
  useEffect(() => {
    const ta = taRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 160)}px`
  }, [draft])

  const toggleMute = async () => {
    try {
      await apiPatch(`/api/chats/${chatId}`, { muted: !muted })
    } catch {
      toast('No se pudo cambiar el silencio')
    }
  }

  const members = chat?.members ?? []
  const showLoadOlder = hasMore && (live?.length ?? 0) >= PAGE

  return (
    <div className={`chat-conv${embedded ? ' embedded' : ''}`}>
      <header className="chat-conv-head">
        {onBack && (
          <button type="button" className="icon-btn chat-back" onClick={onBack} aria-label="Volver a la bandeja">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        <div className="chat-conv-title">
          <strong>{title}</strong>
          {members.length > 0 && (
            <span className="muted">
              {members.length} {members.length === 1 ? 'miembro' : 'miembros'}
            </span>
          )}
        </div>
        {members.length > 0 && <AvatarCluster emails={members.map((e) => nameOf(names, e))} />}
        {muted !== undefined && (
          <button
            type="button"
            className="icon-btn"
            onClick={() => void toggleMute()}
            title={muted ? 'Activar notificaciones' : 'Silenciar conversación'}
            aria-label={muted ? 'Activar notificaciones' : 'Silenciar conversación'}
          >
            {muted ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                <path d="M13.7 21a2 2 0 0 1-3.4 0M18 8a6 6 0 0 0-9.3-5M6.3 6.3C6.1 6.8 6 7.4 6 8c0 7-3 9-3 9h14M21 17h-2M3 3l18 18" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
              </svg>
            )}
          </button>
        )}
        {chat?.type === 'group' && (
          <button
            type="button"
            className="icon-btn"
            onClick={() => setShowMembers(true)}
            title="Miembros del grupo"
            aria-label="Miembros del grupo"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </button>
        )}
        {onClose && (
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Cerrar chat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </header>

      <div className="chat-scroll" ref={listRef} onScroll={onScroll}>
        {live === null ? (
          <div className="chat-skel">
            <div className="skel" style={{ height: 52, width: '55%' }} />
            <div className="skel" style={{ height: 52, width: '45%', marginLeft: 'auto' }} />
            <div className="skel" style={{ height: 52, width: '60%' }} />
          </div>
        ) : (
          <>
            {showLoadOlder && (
              <div className="chat-load-more">
                <button type="button" className="btn btn-ghost" onClick={() => void loadOlder()} disabled={loadingOlder}>
                  {loadingOlder ? 'Cargando…' : 'Cargar anteriores'}
                </button>
              </div>
            )}
            {messages.length === 0 && (
              <p className="state">Aún no hay mensajes. Escribe el primero.</p>
            )}
            {messages.map((msg, i) => {
              const prev = messages[i - 1]
              const newDay = !prev || dayKey(prev.at) !== dayKey(msg.at)
              const own = msg.by === meEmail
              const isTemp = msg.id.startsWith('tmp_')
              const canEditMsg = own && !msg.deletedAt && !isTemp
              const canDeleteMsg = (own || isGroupAdmin) && !msg.deletedAt && !isTemp
              const isEditing = editing?.id === msg.id
              return (
                <div key={msg.id}>
                  {newDay && <div className="chat-day">{dayLabel(msg.at)}</div>}
                  <div className={`chat-bubble${own ? ' own' : ''}${isTemp ? ' sending' : ''}`}>
                    <div className="meta">
                      {/* Roster → personas → espejo (D-25): el Campfire importado
                          trae al autor real; un cliente sale con su empresa. */}
                      <span className="who">
                        {own ? 'Tú' : autor(msg.by, msg.byName).nombre}
                        {!own && <MarcaExterno autor={autor(msg.by, msg.byName)} />}
                      </span>
                      <span className="when">
                        {timeOf(msg.at)}
                        {msg.editedAt && !msg.deletedAt && ' · editado'}
                        {isTemp && ' · enviando…'}
                      </span>
                      <ChipBasecamp doc={msg} />
                      {(canEditMsg || canDeleteMsg) && !isEditing && (
                        <span className="chat-msg-actions">
                          {canEditMsg && (
                            <button
                              type="button"
                              className="chat-msg-act"
                              title="Editar"
                              aria-label="Editar mensaje"
                              onClick={() => setEditing({ id: msg.id, text: msg.text })}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                                <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5z" />
                              </svg>
                            </button>
                          )}
                          {canDeleteMsg && (
                            <button
                              type="button"
                              className="chat-msg-act danger"
                              title="Eliminar"
                              aria-label="Eliminar mensaje"
                              onClick={() => void removeMsg(msg.id)}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                              </svg>
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                    {msg.deletedAt ? (
                      <span className="muted chat-tombstone">mensaje eliminado</span>
                    ) : isEditing ? (
                      <div className="chat-edit">
                        <textarea
                          value={editing.text}
                          onChange={(e) => setEditing({ id: msg.id, text: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault()
                              void saveEdit()
                            }
                            if (e.key === 'Escape') {
                              // Cancelar la edición se queda el Escape: si burbujea,
                              // cierra además el panel del chat (Escape en cascada).
                              e.preventDefault()
                              e.stopPropagation()
                              setEditing(null)
                            }
                          }}
                          rows={2}
                          maxLength={4000}
                          autoFocus
                        />
                        <div className="chat-edit-actions">
                          <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                            Cancelar
                          </button>
                          <button type="button" className="btn btn-primary" onClick={() => void saveEdit()}>
                            Guardar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {msg.imageUrl && (
                          <a className="chat-img" href={msg.imageUrl} target="_blank" rel="noreferrer">
                            <img src={msg.imageUrl} alt="" loading="lazy" />
                          </a>
                        )}
                        <TextoMensaje mensaje={msg} projectId={chat?.projectId} names={names} />
                      </>
                    )}
                    {!msg.deletedAt && !isTemp && !isEditing && (
                      <BuzzBar
                        buzzes={msg.buzzes}
                        me={meEmail}
                        dense
                        onBuzz={(text) => buzzMsg(msg.id, text)}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </>
        )}
      </div>

      <div className="chat-composer-wrap">
        {mention && mentionMatches.length > 0 && (
          <div className="chat-mention-pop" role="listbox" aria-label="Mencionar a alguien">
            {mentionMatches.map((m, i) => (
              <button
                key={m.email}
                type="button"
                role="option"
                aria-selected={i === mentionIdx}
                className={`chat-mention-opt${i === mentionIdx ? ' active' : ''}`}
                onMouseDown={(e) => {
                  e.preventDefault() // que el textarea no pierda el foco
                  pickMention(m)
                }}
              >
                <span className="chip mention">@{m.name}</span>
                {m.fueraDelProyecto ? (
                  <span className="chat-mention-fuera" title={m.email}>
                    no está en el proyecto
                  </span>
                ) : (
                  <span className="muted">{m.email}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {proyecto && (
          <AvisoFueraDelProyecto
            project={proyecto}
            emails={fueraEnviados}
            momento="despues"
            onDescartar={(e) => setFueraEnviados((prev) => prev.filter((x) => x !== e))}
          />
        )}
        {adjunto && (
          <div className="chat-attach" role="status">
            <img src={adjunto.url} alt="" />
            <span className="chat-attach-name">{adjunto.nombre}</span>
            <button type="button" onClick={() => setAdjunto(null)} aria-label="Quitar imagen">
              <IconoCerrar />
            </button>
          </div>
        )}
        <div className="chat-composer">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              void adjuntar(e.target.files?.[0])
              e.target.value = ''
            }}
          />
          <button
            type="button"
            className="chat-attach-btn"
            onClick={() => fileRef.current?.click()}
            disabled={subiendo}
            aria-label="Adjuntar imagen"
            title="Adjuntar imagen"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <rect x="3" y="4.5" width="18" height="15" rx="2" />
              <circle cx="8.5" cy="10" r="1.6" />
              <path d="M4 17l5-4.5 4 3.5 3-2.5 4 3.5" />
            </svg>
          </button>
          <textarea
            ref={taRef}
            onPaste={(e) => {
              // Pegar una captura es la vía más común de mandar una imagen.
              const f = Array.from(e.clipboardData?.files ?? []).find(isImageFile)
              if (f) {
                e.preventDefault()
                void adjuntar(f)
              }
            }}
            value={draft}
            placeholder="Escribe un mensaje… (@ para mencionar)"
            maxLength={4000}
            rows={1}
            onChange={(e) => {
              setDraft(e.target.value)
              detectMention(e.target.value, e.target.selectionStart ?? e.target.value.length)
            }}
            onKeyDown={onComposerKeyDown}
            onBlur={() => window.setTimeout(() => setMention(null), 150)}
          />
          <button
            type="button"
            className="btn btn-primary chat-send"
            onClick={() => void send()}
            disabled={(!draft.trim() && !adjunto) || subiendo}
            aria-label="Enviar mensaje"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
            </svg>
          </button>
        </div>
      </div>

      {showMembers && chat && chat.type === 'group' && (
        <MembersModal
          chat={chat}
          meEmail={meEmail}
          names={names}
          team={team}
          onClose={() => setShowMembers(false)}
          onLeft={() => {
            setShowMembers(false)
            onLeft?.()
          }}
          toast={toast}
        />
      )}
    </div>
  )
}
